import React, {useState, useEffect, useRef, useMemo} from "react";
import PropTypes from "prop-types";
import {useDispatch, useSelector} from "react-redux";

//actions:
import {mediaItemsListActions} from "../../../redux/MediaItemsList/mediaItemsListRedux";
import {getVideoFiles} from "../../../redux/Video/VideoCrud";

//components:
import {makeStyles} from "@material-ui/core/styles";
import SweetAlert from "react-bootstrap-sweetalert";
import {Modal, Backdrop, Fade, CircularProgress} from "@material-ui/core";
import HighlightOffRoundedIcon from "@material-ui/icons/HighlightOffRounded";
import Cropper from "react-cropper";
import {UppyImageDragDrop} from "../../uppy/UppyImageDragDrop";
import {CustomDropdown, VideosSearchSlide, SimpleVideoSlide} from "../../index";

//helpers:
import {getFakeVideoSearchResults} from "./fakeSearchCardMatches";
import {useTranslation} from "react-i18next";
import {getImageFromFilesbyKey, getImagesFromFilesbyKey} from "../../../../_metronic/_helpers";

//styles:
import "cropperjs/dist/cropper.css";

const useStyles = makeStyles(theme => ({
    modal: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0, 0, 0, 0.5)",
    },
    paper: {
        backgroundColor: theme.palette.background.paper,
        border: "none",
        borderRadius: "0.5rem",
        boxShadow: theme.shadows[5],
    },
}));

// !! This should be taken from the API Call !!
const imageTypeOptions = [
    {id: 2, key: "imgPortrait", name: "Portrait", ratio: 5 / 6},
    {id: 3, key: "imgLandscape", name: "Landscape", ratio: 12 / 5},
    {id: 5, key: "imgSquare", name: "Square", ratio: 1},
];

export const VideoThumbnailEditor = ({
    id = undefined,
    video = {},
    open = false,
    setOpen = () => {},
    onUpload = () => {},
    setVideoGeneralData = () => {},
    onDeleteImage = () => {},
}) => {
    const {t} = useTranslation();
    const classes = useStyles();

    //redux:
    //store:
    const token = ""; //TODO remove-me;
    //actions:
    const dispatch = useDispatch();
    const getAllMediaItems = token => dispatch(mediaItemsListActions.getAllMediaItems(token));

    //local state:
    const [suggestedImgs, setSuggestedImgs] = useState([]);
    const [uploadedImgs, setUploadedImgs] = useState([]);

    const [imgToCrop, setImgToCrop] = useState("");

    const [message, setMessage] = useState("");
    const [selectedImgType, setSelectedImgType] = useState([(imageTypeOptions && imageTypeOptions[0]) || null]);

    const [showUploadedModal, setShowUploadedModal] = useState(false);
    const [showDeletedModal, setShowDeletedModal] = useState(false);
    const [showFailedModal, setShowFailedModal] = useState(false);

    const [loading, setLoading] = useState(false);

    const [ratio, setRatio] = useState(imageTypeOptions[0].ratio);
    const [showCropper, setShowCropper] = useState(true);

    const [currentSquareImg, setCurrentSquareImg] = useState("");

    useEffect(() => {
        if (video && video.files && open === true) {
            const videoFiles = video.files || [];

            const _suggested = getImagesFromFilesbyKey(videoFiles, video.id, "imgProposed", video.type_id);

            const _uploaded = getImagesFromFilesbyKey(videoFiles, video.id, "imgUploaded", video.type_id);

            if (_suggested.length > 0) {
                setSuggestedImgs(_suggested.slice(0, 6));
            }
            setUploadedImgs(_uploaded);

            const selectedType = (selectedImgType && selectedImgType[0] && selectedImgType[0].key) || "";

            const imageOfSelectedType = getImageFromFilesbyKey(videoFiles, video.id, selectedType, video.type_id);

            const _squareImg = getImagesFromFilesbyKey(videoFiles, video.id, "imgSquare", video.type_id);
            const _squareImgSrc = _squareImg?.[0]?.public_url || (video.type_id===1 ? "/media/dummy/video_dummy.png" : "/media/dummy/document_dummy.png");

            setCurrentSquareImg(_squareImgSrc);

            setImgToCrop(imageOfSelectedType);

            setLoading(false);
        } else {
            setTimeout(() => {
                setImgToCrop("");
                setMessage("");
            }, 800);
            setLoading(false);
        }
    }, [video, open]);

    useEffect(() => {
        if (imgToCrop) {
            const selectedObj = selectedImgType && selectedImgType[0];

            if (selectedObj) {
                const _ratio = selectedObj.ratio;

                if (_ratio) {
                    setRatio(_ratio);
                }

                setShowCropper(false);

                setTimeout(() => {
                    setShowCropper(true);
                }, 1);
            } else setShowCropper(false);
        }
    }, [imgToCrop, selectedImgType]);

    const cropperRef = useRef(null);
    const handleUploadCrop = async () => {
        const imageElement = cropperRef?.current;
        const cropper = imageElement?.cropper;
        const canvas = cropper.getCroppedCanvas();
        const type = (selectedImgType && selectedImgType[0] && selectedImgType[0].key) || "";

        onUpload({
            id,
            file: {type, canvas},
            onConfirmUploaded: () => {
                setShowUploadedModal(true);
                setLoading(false);
            },
            onError: () => {
                setShowFailedModal(true);
                setLoading(false);
            },
        });
        setLoading(true);
    };

    const handleDropdownChange = optionsArr => {
        const option = optionsArr && optionsArr[0];
        if (option) {
            const imgOfSelectedType = getImagesFromFilesbyKey(video.files, video.id, option.key, video.type_id);

            if (imgOfSelectedType && imgOfSelectedType[0] && imgOfSelectedType[0].public_url) {
                setImgToCrop(imgOfSelectedType[0].public_url);
            }
            setSelectedImgType([option]);
        }
    };

    const updateVideoFiles = async () => {
        const files = await getVideoFiles(video.id)
        setVideoGeneralData({...video, files: files})
    }

    const selectedImgTypeID = selectedImgType?.[0]?.key;

    return (
        <Modal
            aria-labelledby="transition-modal-title"
            aria-describedby="transition-modal-description"
            className={`${classes.modal} uploadmodal`}
            open={open}
            onClose={() => setOpen(false)}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{
                timeout: 500,
            }}
        >
            <Fade in={open} unmountOnExit={true} mountOnEnter={true}>
                <div className={`video_image_editor_modal border-focus-0 card card-custom col-9 ${classes.paper}`}>
                    {loading || showUploadedModal || showDeletedModal || showFailedModal ? (
                        <div className="position-fixed w-100vw h-100vh top-0 left-0 z-index-100 pointer-events-none" />
                    ) : null}
                    {loading ? (
                        <div className="w-100 h-100 d-flex justify-content-center align-items-center position-absolute z-index-2">
                            <CircularProgress />
                        </div>
                    ) : null}
                    <div className="card-header w-100 flex-wrap pb-0 border-bottom border-gray justify-content-between">
                        <h3 className="card-title text-uppercase">{t("MODAL.VIDEO_IMAGE_SELECTOR.TITLE")}</h3>
                        <button
                            type="button"
                            className="close font-size-base"
                            data-dismiss="modal"
                            aria-label="Close"
                            onClick={() => setOpen(false)}
                        >
                            <HighlightOffRoundedIcon fontSize="large" />
                        </button>
                    </div>
                    <div className="card-body w-100 pb-0" style={{overflowY: "scroll"}}>
                        <div className="d-flex justify-content-between">
                            {/* left side: */}
                            <div className="d-flex flex-column p-4 col-12 col-md-3">
                                <h5 className="text-muted">{t("MODAL.VIDEO_IMAGE_SELECTOR.SELECT")}</h5>
                                <CustomDropdown
                                    className="p-0"
                                    placeholder={t("MODAL.VIDEO_IMAGE_SELECTOR.SELECT.PLACEHOLDER")}
                                    optionsMaxWidth
                                    variant="light"
                                    options={imageTypeOptions || []}
                                    selected={selectedImgType || null}
                                    setSelected={handleDropdownChange}
                                />
                                <div className="d-flex justify-content-center flex-column">
                                    <div className="d-flex flex-column">
                                        <h6 className="text-muted">{t("MODAL.VIDEO_IMAGE_SELECTOR.CURRENT")}</h6>
                                        <div className="previews_container">
                                            {selectedImgTypeID === "imgPortrait" ? (
                                                <div className="reducted_simple_card">
                                                    <SimpleVideoSlide preview={false} video={video || {}} />
                                                </div>
                                            ) : null}
                                            {selectedImgTypeID === "imgLandscape" ? (
                                                <div className="reducted_videoSearch_card">
                                                    <VideosSearchSlide
                                                        preview={false}
                                                        video={getFakeVideoSearchResults(video)}
                                                        query="lorem"
                                                    />
                                                </div>
                                            ) : null}
                                            {selectedImgTypeID === "imgSquare" ? (
                                                <div
                                                    className="rounded-circle border border-gray w-200px h-200px m-auto overflow-hidden bg_image_cover"
                                                    style={{
                                                        backgroundImage: `url(${currentSquareImg})`,
                                                    }}
                                                />
                                            ) : null}
                                        </div>
                                        <h6 className="text-muted">{t("MODAL.VIDEO_IMAGE_SELECTOR.PREVIEW")}</h6>
                                        <div className="previews_container">
                                            {selectedImgTypeID === "imgPortrait" ? (
                                                <div className="reducted_simple_card">
                                                    <SimpleVideoSlide preview={true} video={video} />
                                                </div>
                                            ) : null}
                                            {selectedImgTypeID === "imgLandscape" ? (
                                                <div className="reducted_videoSearch_card">
                                                    <VideosSearchSlide
                                                        preview={true}
                                                        video={getFakeVideoSearchResults(video)}
                                                        query="lorem"
                                                    />
                                                </div>
                                            ) : null}
                                            {selectedImgTypeID === "imgSquare" ? (
                                                <div
                                                    className="rounded-circle border border-gray w-200px h-200px m-auto cropped-preview overflow-hidden bg_image_cover"
                                                    style={{
                                                        backgroundImage: `url(${imgToCrop})`,
                                                    }}
                                                />
                                            ) : null}
                                        </div>
                                    </div>
                                </div>
                                <button
                                    className="btn btn-primary font-weight-bolder font-size-h6 py-3 px-7 my-3 w-100"
                                    disabled={!imgToCrop}
                                    onClick={handleUploadCrop}
                                >
                                    {t("GENERAL.APPLY")}
                                </button>
                            </div>
                            {/* right side: */}
                            <div className="d-flex flex-column p-4 col-12 col-md-8">
                                {/* cropper: */}
                                <div className="position-relative" style={{height: 400, width: "100%"}}>
                                    {useMemo(
                                        () =>
                                            showCropper ? (
                                                <Cropper
                                                    style={{height: "100%", width: "100%"}}
                                                    ref={cropperRef}
                                                    aspectRatio={ratio}
                                                    preview=".cropped-preview"
                                                    guides={false}
                                                    src={imgToCrop}
                                                    viewMode={1}
                                                    dragMode="move"
                                                    crossOrigin='anonymous'
                                                    checkCrossOrigin={false}
                                                    checkOrientation={false}
                                                    cropBoxMovable={false}
                                                    autoCropArea={0.9}
                                                />
                                            ) : null,
                                        [showCropper, imgToCrop, ratio]
                                    )}
                                </div>

                                {/* suggested thumbnails or new one: */}
                                <div className="d-flex w-100 mt-8 flex-column">
                                    <h5 className="text-muted text-uppercase mb-4">
                                        {t("MODAL.VIDEO_IMGAE_SELECTOR.CAPTURES.TITLE", {
                                            name: video?.title ? `"${video.title}"` : "",
                                        })}
                                    </h5>
                                    <div className="d-flex flex-column flex-wrap">
                                        <div className="d-flex align-items-center w-100 p-0 h-90px overflow-x-scroll">
                                            {suggestedImgs.length ? (
                                                suggestedImgs.map((capture, i) => (
                                                    <div key={i} className="preview_imgs gutter-b">
                                                        <img
                                                            className="cursor-pointer border rounded"
                                                            src={capture.public_url}
                                                            onClick={() => setImgToCrop(capture.public_url)}
                                                        />
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="text-muted font-weight-bold">
                                                    {t("MODAL.VIDEO_IMAGE_SELECTOR.NO_CAPTURES")}
                                                </div>
                                            )}
                                        </div>

                                        {/* uploaded images: */}
                                        <div className="d-flex w-100 mt-6 flex-column">
                                            <h5 className="text-muted text-uppercase mb-4">
                                                {t("MODAL.VIDEO_IMAGE_SELECTOR.UPLOADED")}
                                            </h5>
                                            <div className="d-flex align-items-center w-100 p-0 overflow-y-scroll">
                                                <div className="d-flex align-self-start">
                                                    <UppyImageDragDrop
                                                        video_id={video?.id}
                                                        id="videoImageUpload"
                                                        uploadType="videoThumbnailUpload"
                                                        height="100px"
                                                        margin="0 10px 0 0"
                                                        onSuccess={() => {
                                                            setTimeout(() => {
                                                                getAllMediaItems(token);
                                                                setShowUploadedModal(true);
                                                                updateVideoFiles();
                                                                setLoading(false);
                                                            }, 1000);

                                                            setLoading(true);
                                                        }}
                                                        onFail={() => {
                                                            setShowFailedModal(true);
                                                            setLoading(false);
                                                        }}
                                                    />
                                                </div>
                                                <div className="d-flex flex-wrap w-100">
                                                    {uploadedImgs.length ? (
                                                        uploadedImgs.map((uploadedFile, i) => (
                                                            <div key={i} className="preview_imgs gutter-b">
                                                                <img
                                                                    className="cursor-pointer border rounded"
                                                                    src={uploadedFile.public_url}
                                                                    onClick={() =>
                                                                        setImgToCrop(uploadedFile.public_url)
                                                                    }
                                                                />
                                                                <button
                                                                    type="button"
                                                                    className="close deleteBtnUploadedImage bg-danger"
                                                                    aria-label="Delete"
                                                                    onClick={() => {
                                                                        onDeleteImage({
                                                                            videoId: video.id,
                                                                            fileId: uploadedFile.id,
                                                                            onConfirmDeleted: () =>
                                                                                setShowDeletedModal(true),
                                                                            onErrorCall: () => setShowFailedModal(true),
                                                                        });
                                                                        setLoading(true);
                                                                    }}
                                                                >
                                                                    <HighlightOffRoundedIcon fontSize="small" />
                                                                </button>
                                                            </div>
                                                        ))
                                                    ) : (
                                                        <div className="text-muted font-weight-bold">
                                                            {t("MODAL.VIDEO_IMAGE_SELECTOR.NONE_UPLOADED")}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <p className="py-4 m-0 text-danger">{message}</p>
                        <SweetAlert
                            show={showUploadedModal}
                            success
                            title={t("MODAL.IMAGE.UPLOAD.SUCCESS")}
                            confirmBtnText={t("GENERAL.OK")}
                            confirmBtnBsStyle="primary"
                            onConfirm={() => {
                                setShowUploadedModal(false);
                            }}
                        />
                        <SweetAlert
                            show={showDeletedModal}
                            success
                            title={t("MODAL.VIDEO_IMAGE_SELECTOR.DELETED.SUCCESS")}
                            confirmBtnText={t("GENERAL.OK")}
                            confirmBtnBsStyle="primary"
                            onConfirm={() => {
                                setShowDeletedModal(false);
                            }}
                        />
                        <SweetAlert
                            show={showFailedModal}
                            error
                            title={t("MODAL.IMAGE.UPLOAD.ERROR")}
                            confirmBtnText={t("GENERAL.OK")}
                            confirmBtnBsStyle="primary"
                            onConfirm={() => {
                                setShowFailedModal(false);
                            }}
                        />
                    </div>
                </div>
            </Fade>
        </Modal>
    );
};

VideoThumbnailEditor.propTypes = {
    id: PropTypes.string,
    video: PropTypes.object,
    open: PropTypes.bool.isRequired,
    setOpen: PropTypes.func.isRequired,
    onUpload: PropTypes.func,
    onDeleteImage: PropTypes.func,
};
